import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import {
  Scissors, LogOut, RefreshCw, Trash2, Check, X, Clock, Users,
  Calendar as CalIcon, Euro, ChevronLeft, ChevronRight, Phone, Mail,
  List, LayoutGrid, MessageCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'jr_admin_token';

const FR_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const FR_MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const statusColors = {
  pending: 'bg-[#c9a961]/20 text-[#c9a961] border-[#c9a961]/40',
  confirmed: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40',
  cancelled: 'bg-red-900/40 text-red-300 border-red-700/40',
};

const statusLabel = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  cancelled: 'Annulé',
};

const statusDot = {
  pending: 'bg-[#c9a961]',
  confirmed: 'bg-emerald-400',
  cancelled: 'bg-red-400',
};

// ---- Helpers ----
const fmtISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const buildMonthGrid = (viewDate) => {
  const first = startOfMonth(viewDate);
  const dayOfWeek = (first.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const cells = [];
  // Previous month tail
  for (let i = dayOfWeek - 1; i >= 0; i--) {
    const d = new Date(first);
    d.setDate(-i);
    cells.push({ date: d, outside: true });
  }
  // Current month
  for (let i = 1; i <= daysInMonth; i++) {
    cells.push({ date: new Date(viewDate.getFullYear(), viewDate.getMonth(), i), outside: false });
  }
  // Next month head
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, outside: true });
  }
  return cells;
};

// ---- Main Component ----
const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [viewMode, setViewMode] = useState('calendar'); // calendar | list
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(fmtISO(new Date()));
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const authHeaders = useMemo(
    () => ({ headers: { 'X-Admin-Token': token } }),
    [token]
  );

  // ---- Auth ----
  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast({ title: 'Connecté', description: 'Bienvenue dans le back-office.' });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Mot de passe incorrect',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setBookings([]);
  };

  const fetchBookings = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, authHeaders);
      setBookings(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      toast({ title: 'Erreur', description: 'Impossible de charger les réservations', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); /* eslint-disable-next-line */ }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, authHeaders);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast({ title: 'Statut mis à jour' });
    } catch {
      toast({ title: 'Erreur', description: 'Mise à jour impossible', variant: 'destructive' });
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm('Supprimer définitivement cette réservation ?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, authHeaders);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast({ title: 'Réservation supprimée' });
    } catch {
      toast({ title: 'Erreur', description: 'Suppression impossible', variant: 'destructive' });
    }
  };

  // ---- Derived data ----
  const bookingsByDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!map[b.date]) map[b.date] = [];
      map[b.date].push(b);
    }
    // Sort each day by time
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return map;
  }, [bookings]);

  const monthCells = useMemo(() => buildMonthGrid(viewDate), [viewDate]);
  const todayISO = fmtISO(new Date());

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (filter !== 'all' && b.status !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          b.name.toLowerCase().includes(q) ||
          b.phone.toLowerCase().includes(q) ||
          (b.email || '').toLowerCase().includes(q) ||
          b.service.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [bookings, filter, search]);

  const stats = useMemo(() => {
    const today = todayISO;
    const upcoming = bookings.filter((b) => b.date >= today && b.status !== 'cancelled');
    const revenue = bookings
      .filter((b) => b.status !== 'cancelled')
      .reduce((s, b) => s + (b.price || 0), 0);
    return {
      total: bookings.length,
      pending: bookings.filter((b) => b.status === 'pending').length,
      confirmed: bookings.filter((b) => b.status === 'confirmed').length,
      upcoming: upcoming.length,
      revenue,
    };
  }, [bookings, todayISO]);

  const selectedDayBookings = bookingsByDate[selectedDay] || [];

  // ---- LOGIN VIEW ----
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <form onSubmit={login} className="w-full max-w-md bg-[#141110] border border-[#c9a961]/25 p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-full border border-[#c9a961]/60 flex items-center justify-center">
              <Scissors className="w-5 h-5 text-[#c9a961]" />
            </div>
            <div>
              <div className="font-display text-2xl text-[#f5efe4]">JR Barber</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#c9a961]">Back-office</div>
            </div>
          </div>
          <h1 className="font-display text-3xl text-[#f5efe4] mb-2">Connexion admin</h1>
          <p className="text-[#a8a29e] text-sm mb-8">Entrez le mot de passe pour accéder aux réservations.</p>
          <Label htmlFor="pwd" className="text-[#c9a961] uppercase text-xs tracking-widest">Mot de passe</Label>
          <Input
            id="pwd"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••"
            autoFocus
            className="mt-3 bg-[#0a0a0a] border-[#c9a961]/20 text-[#f5efe4] rounded-none h-12"
          />
          <Button
            type="submit"
            disabled={loading || !password}
            className="w-full mt-8 bg-[#c9a961] hover:bg-[#d9b972] disabled:opacity-40 text-[#0a0a0a] font-semibold rounded-none h-12"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
          <a href="/" className="block text-center mt-6 text-xs text-[#a8a29e] hover:text-[#c9a961]">
            ← Retour au site
          </a>
        </form>
        <Toaster />
      </div>
    );
  }

  // ---- DASHBOARD ----
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5efe4]">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-[#c9a961]/15 bg-[#0a0a0a]/95 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border border-[#c9a961]/60 flex items-center justify-center">
              <Scissors className="w-4 h-4 text-[#c9a961]" />
            </div>
            <div>
              <div className="font-display text-lg leading-tight">JR Barber</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#c9a961]">Back-office</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center bg-[#141110] border border-[#c9a961]/20 rounded-none p-0.5">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-[#c9a961] text-[#0a0a0a]'
                    : 'text-[#c8beab] hover:text-[#c9a961]'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Calendrier
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[#c9a961] text-[#0a0a0a]'
                    : 'text-[#c8beab] hover:text-[#c9a961]'
                }`}
              >
                <List className="w-3.5 h-3.5" /> Liste
              </button>
            </div>
            <Button
              variant="outline"
              onClick={fetchBookings}
              disabled={loading}
              className="border-[#c9a961]/40 bg-transparent text-[#f5efe4] hover:bg-[#c9a961]/10 hover:text-[#c9a961] rounded-none"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              className="border-[#c9a961]/40 bg-transparent text-[#f5efe4] hover:bg-[#c9a961]/10 hover:text-[#c9a961] rounded-none"
            >
              <LogOut className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          <StatCard icon={CalIcon} label="Total" value={stats.total} />
          <StatCard icon={Clock} label="À venir" value={stats.upcoming} color="text-[#c9a961]" />
          <StatCard icon={MessageCircle} label="En attente" value={stats.pending} color="text-[#c9a961]" />
          <StatCard icon={Users} label="Confirmées" value={stats.confirmed} color="text-emerald-400" />
          <StatCard icon={Euro} label="CA estimé" value={`${stats.revenue} €`} color="text-[#c9a961]" />
        </div>

        {/* Mobile view toggle */}
        <div className="md:hidden flex items-center bg-[#141110] border border-[#c9a961]/20 rounded-none p-0.5 mb-6">
          <button
            onClick={() => setViewMode('calendar')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium ${
              viewMode === 'calendar' ? 'bg-[#c9a961] text-[#0a0a0a]' : 'text-[#c8beab]'
            }`}
          >
            <LayoutGrid className="w-4 h-4" /> Calendrier
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium ${
              viewMode === 'list' ? 'bg-[#c9a961] text-[#0a0a0a]' : 'text-[#c8beab]'
            }`}
          >
            <List className="w-4 h-4" /> Liste
          </button>
        </div>

        {viewMode === 'calendar' ? (
          <CalendarView
            viewDate={viewDate}
            setViewDate={setViewDate}
            monthCells={monthCells}
            bookingsByDate={bookingsByDate}
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
            selectedDayBookings={selectedDayBookings}
            todayISO={todayISO}
            updateStatus={updateStatus}
            removeBooking={removeBooking}
          />
        ) : (
          <ListView
            filtered={filtered}
            loading={loading}
            search={search}
            setSearch={setSearch}
            filter={filter}
            setFilter={setFilter}
            updateStatus={updateStatus}
            removeBooking={removeBooking}
          />
        )}
      </main>
      <Toaster />
    </div>
  );
};

// ---- Calendar View ----
const CalendarView = ({
  viewDate, setViewDate, monthCells, bookingsByDate,
  selectedDay, setSelectedDay, selectedDayBookings, todayISO,
  updateStatus, removeBooking
}) => {
  const monthTitle = `${FR_MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`;
  const goPrev = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const goNext = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDay(fmtISO(now));
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-6">
      {/* Calendar */}
      <div className="bg-[#141110] border border-[#c9a961]/15">
        <div className="flex items-center justify-between p-5 border-b border-[#c9a961]/15">
          <h2 className="font-display text-2xl md:text-3xl text-[#f5efe4] capitalize">
            {monthTitle}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={goToday}
              className="text-xs uppercase tracking-widest px-3 py-2 border border-[#c9a961]/30 text-[#c9a961] hover:bg-[#c9a961]/10 transition-colors"
            >
              Aujourd'hui
            </button>
            <button
              onClick={goPrev}
              className="w-9 h-9 border border-[#c9a961]/30 text-[#f5efe4] hover:bg-[#c9a961]/10 hover:text-[#c9a961] flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goNext}
              className="w-9 h-9 border border-[#c9a961]/30 text-[#f5efe4] hover:bg-[#c9a961]/10 hover:text-[#c9a961] flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-[#c9a961]/15">
          {FR_DAYS.map((d) => (
            <div key={d} className="text-center py-3 text-[10px] tracking-widest uppercase text-[#c9a961]">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {monthCells.map((cell, i) => {
            const iso = fmtISO(cell.date);
            const isToday = iso === todayISO;
            const isSelected = iso === selectedDay;
            const list = bookingsByDate[iso] || [];
            const total = list.length;
            const isSunday = cell.date.getDay() === 0;

            return (
              <button
                key={i}
                onClick={() => setSelectedDay(iso)}
                className={`relative min-h-[92px] md:min-h-[110px] p-2 md:p-3 text-left border-b border-r border-[#c9a961]/10 transition-colors ${
                  cell.outside ? 'bg-[#0e0c0a] text-[#5a5140]' : 'text-[#f5efe4] hover:bg-[#c9a961]/8'
                } ${isSelected ? 'ring-1 ring-inset ring-[#c9a961] bg-[#c9a961]/8' : ''} ${
                  isSunday && !cell.outside ? 'bg-[#0f0d0a]' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex items-center justify-center text-sm font-medium ${
                      isToday ? 'bg-[#c9a961] text-[#0a0a0a] w-7 h-7 rounded-full' : ''
                    }`}
                  >
                    {cell.date.getDate()}
                  </span>
                  {total > 0 && !cell.outside && (
                    <span className="text-[10px] font-bold text-[#c9a961]">
                      {total}
                    </span>
                  )}
                </div>
                {!cell.outside && list.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {list.slice(0, 3).map((b) => (
                      <div
                        key={b.id}
                        className="flex items-center gap-1.5 text-[10px] md:text-[11px] truncate"
                        title={`${b.time} — ${b.name}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot[b.status]}`} />
                        <span className="text-[#c9a961] font-mono">{b.time}</span>
                        <span className="text-[#c8beab] truncate hidden md:inline">{b.name}</span>
                      </div>
                    ))}
                    {list.length > 3 && (
                      <div className="text-[10px] text-[#a8a29e] pl-3">+{list.length - 3}</div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3 border-t border-[#c9a961]/15 text-[11px] text-[#a8a29e]">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#c9a961]" /> En attente
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" /> Confirmé
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400" /> Annulé
          </span>
        </div>
      </div>

      {/* Day details */}
      <div className="bg-[#141110] border border-[#c9a961]/15 flex flex-col">
        <div className="p-5 border-b border-[#c9a961]/15">
          <div className="text-[10px] tracking-widest uppercase text-[#c9a961] mb-1">
            Détail du jour
          </div>
          <div className="font-display text-xl text-[#f5efe4] capitalize">
            {new Date(selectedDay + 'T00:00:00').toLocaleDateString('fr-FR', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })}
          </div>
          <div className="text-xs text-[#a8a29e] mt-1">
            {selectedDayBookings.length} réservation{selectedDayBookings.length > 1 ? 's' : ''}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto max-h-[720px]">
          {selectedDayBookings.length === 0 ? (
            <div className="p-8 text-center">
              <CalIcon className="w-10 h-10 text-[#c9a961]/30 mx-auto" />
              <div className="mt-4 text-sm text-[#a8a29e]">Aucune réservation ce jour.</div>
            </div>
          ) : (
            selectedDayBookings.map((b) => (
              <BookingDetailCard
                key={b.id}
                b={b}
                onUpdate={updateStatus}
                onDelete={removeBooking}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const BookingDetailCard = ({ b, onUpdate, onDelete }) => (
  <div className="p-5 border-b border-[#c9a961]/10 last:border-b-0 hover:bg-[#0a0a0a]/30">
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="font-display text-2xl text-[#c9a961]">{b.time}</span>
          <Badge className={`${statusColors[b.status]} border rounded-none uppercase text-[9px] tracking-widest px-2 py-0.5`}>
            {statusLabel[b.status]}
          </Badge>
        </div>
        <div className="mt-2 font-medium text-[#f5efe4]">{b.name}</div>
        <div className="text-[13px] text-[#c8beab] mt-1">{b.service} · <span className="text-[#c9a961]">{b.price} €</span></div>
      </div>
    </div>

    <div className="mt-3 space-y-1.5 text-[13px]">
      <div className="flex items-center gap-2 text-[#c8beab]">
        <Phone className="w-3.5 h-3.5 text-[#c9a961]/70" />
        <a href={`tel:${b.phone}`} className="hover:text-[#c9a961]">{b.phone}</a>
      </div>
      {b.email && (
        <div className="flex items-center gap-2 text-[#c8beab] truncate">
          <Mail className="w-3.5 h-3.5 text-[#c9a961]/70" />
          <a href={`mailto:${b.email}`} className="hover:text-[#c9a961] truncate">{b.email}</a>
        </div>
      )}
      {b.notes && (
        <div className="mt-2 text-xs text-[#a8a29e] italic border-l-2 border-[#c9a961]/25 pl-2">
          « {b.notes} »
        </div>
      )}
    </div>

    <div className="mt-4 flex items-center gap-2">
      {b.status !== 'confirmed' && (
        <button
          onClick={() => onUpdate(b.id, 'confirmed')}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs border border-emerald-700/40 text-emerald-400 hover:bg-emerald-900/30"
        >
          <Check className="w-3.5 h-3.5" /> Confirmer
        </button>
      )}
      {b.status !== 'cancelled' && (
        <button
          onClick={() => onUpdate(b.id, 'cancelled')}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs border border-red-800/40 text-red-400 hover:bg-red-900/30"
        >
          <X className="w-3.5 h-3.5" /> Annuler
        </button>
      )}
      <button
        onClick={() => onDelete(b.id)}
        className="px-3 py-2 border border-[#c9a961]/20 text-[#a8a29e] hover:text-red-400 hover:border-red-800/40"
        title="Supprimer"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

// ---- List View ----
const ListView = ({
  filtered, loading, search, setSearch, filter, setFilter,
  updateStatus, removeBooking,
}) => (
  <>
    <div className="flex flex-col md:flex-row gap-3 mb-5">
      <Input
        placeholder="Rechercher (nom, téléphone, email, prestation)..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="bg-[#141110] border-[#c9a961]/20 text-[#f5efe4] rounded-none h-11 flex-1"
      />
      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="bg-[#141110] border-[#c9a961]/20 text-[#f5efe4] rounded-none h-11 md:w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#141110] border-[#c9a961]/20 text-[#f5efe4]">
          <SelectItem value="all">Tous les statuts</SelectItem>
          <SelectItem value="pending">En attente</SelectItem>
          <SelectItem value="confirmed">Confirmées</SelectItem>
          <SelectItem value="cancelled">Annulées</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="bg-[#141110] border border-[#c9a961]/15 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-[#c9a961]/15 hover:bg-transparent">
            <TableHead className="text-[#c9a961] uppercase text-[10px] tracking-widest">Client</TableHead>
            <TableHead className="text-[#c9a961] uppercase text-[10px] tracking-widest">Contact</TableHead>
            <TableHead className="text-[#c9a961] uppercase text-[10px] tracking-widest">Prestation</TableHead>
            <TableHead className="text-[#c9a961] uppercase text-[10px] tracking-widest">Créneau</TableHead>
            <TableHead className="text-[#c9a961] uppercase text-[10px] tracking-widest">Statut</TableHead>
            <TableHead className="text-[#c9a961] uppercase text-[10px] tracking-widest text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-16 text-[#a8a29e]">
                {loading ? 'Chargement...' : 'Aucune réservation'}
              </TableCell>
            </TableRow>
          )}
          {filtered.map((b) => (
            <TableRow key={b.id} className="border-b border-[#c9a961]/10 hover:bg-[#0a0a0a]/40">
              <TableCell>
                <div className="font-medium text-[#f5efe4]">{b.name}</div>
                {b.notes && (
                  <div className="text-xs text-[#a8a29e] mt-1 max-w-[220px] truncate italic" title={b.notes}>
                    « {b.notes} »
                  </div>
                )}
              </TableCell>
              <TableCell className="text-sm text-[#c8beab]">
                <div>{b.phone}</div>
                {b.email && <div className="text-xs text-[#a8a29e]">{b.email}</div>}
              </TableCell>
              <TableCell>
                <div className="text-[#f5efe4]">{b.service}</div>
                <div className="text-xs text-[#c9a961]">{b.price} €</div>
              </TableCell>
              <TableCell>
                <div className="text-[#f5efe4] text-sm">
                  {new Date(b.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                </div>
                <div className="font-display text-lg text-[#c9a961] leading-tight">{b.time}</div>
              </TableCell>
              <TableCell>
                <Badge className={`${statusColors[b.status]} border rounded-none uppercase text-[10px] tracking-widest px-2 py-1`}>
                  {statusLabel[b.status]}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {b.status !== 'confirmed' && (
                    <button
                      onClick={() => updateStatus(b.id, 'confirmed')}
                      title="Confirmer"
                      className="p-2 border border-emerald-700/40 text-emerald-400 hover:bg-emerald-900/30"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {b.status !== 'cancelled' && (
                    <button
                      onClick={() => updateStatus(b.id, 'cancelled')}
                      title="Annuler"
                      className="p-2 border border-red-800/40 text-red-400 hover:bg-red-900/30"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => removeBooking(b.id)}
                    title="Supprimer"
                    className="p-2 border border-[#c9a961]/20 text-[#a8a29e] hover:text-red-400 hover:border-red-800/40"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </>
);

const StatCard = ({ icon: Icon, label, value, color = 'text-[#f5efe4]' }) => (
  <div className="bg-[#141110] border border-[#c9a961]/15 p-4">
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-widest text-[#a8a29e]">{label}</div>
      <Icon className="w-4 h-4 text-[#c9a961]/60" />
    </div>
    <div className={`font-display text-2xl md:text-3xl mt-2 ${color}`}>{value}</div>
  </div>
);

export default Admin;
